import { Link } from "react-router-dom";
import { CASES } from "../data/portfolio";
import { FULL_STACK, StackLogo } from "../components/StackLogo";
import { RepoCatalog } from "../components/RepoCatalog";
import { ContactSection } from "../components/ContactSection";

const STACK_ROW_A = FULL_STACK.filter((_, i) => i % 2 === 0);
const STACK_ROW_B = FULL_STACK.filter((_, i) => i % 2 === 1);

export function Portfolio() {
  return (
    <div className="portfolio-page">
      <nav className="pf-nav">
        <div className="pf-container pf-nav-inner">
          <Link to="/" className="pf-brand">
            <span className="pf-brand-mark">A</span>
            <span>AUTOMATRIX</span>
          </Link>
          <div className="pf-nav-links">
            <a href="#about">About</a>
            <a href="#work">Projects</a>
            <a href="#repos">Repositories (168)</a>
            <a href="#philosophy">Philosophy</a>
            <a href="#stack">Stack</a>
            <a href="#contact">Contact</a>
          </div>
          <Link className="btn primary btnlink" to="/">
            Book Consultation
          </Link>
        </div>
      </nav>

      <header className="pf-hero">
        <div className="pf-container">
          <span className="eyebrow">Portfolio</span>
          <h1>
            <span className="gradient-text">AI infrastructure</span>
            <br />
            automation architecture &amp; agents.
          </h1>
          <p className="lead-copy">
            Lucas F. N. Alves, founder of Automatrix. Systems architect focused on deterministic
            automation, API orchestration, CRM intelligence, AI agents, and production-grade
            implementation.
          </p>
          <p className="pf-origin-line">
            Based in Brazil, building production systems for clients across Brazil and the US —
            from WhatsApp-native SDR agents to full-stack AI recruitment platforms.
          </p>
          <div className="pf-hero-actions">
            <a href="#work" className="btn primary btnlink">
              View Selected Work
            </a>
            <a href="#stack" className="btn ghost btnlink">
              See the Stack
            </a>
          </div>
        </div>
      </header>

      <section id="about" className="pf-section pf-about">
        <div className="pf-container pf-about-grid">
          <div className="pf-about-copy">
            <span className="eyebrow">About</span>
            <h2>From a lan house to Silicon Valley.</h2>
            <p className="lead-copy">
              Lucas started working with technology at 16, in a small lan house in interior Brazil.
              By his early twenties he was leading technology for a government-contracts business,
              growing it from a small operation into a fast-scaling, eight-person team over four years —
              working federal bids for agencies, ministries and public universities.
            </p>
            <p className="lead-copy">
              He later spent time in Silicon Valley and studied independently at Stanford — marketing,
              algorithms, growth systems — before returning to Brazil to found Automatrix: a studio
              that builds production-grade AI infrastructure instead of demos.
            </p>
            <p className="pf-origin-line">
              18 years in tech. Bilingual, Brazil-based, delivering to clients across Brazil and the US.
            </p>
          </div>
          <div className="pf-about-stats">
            <div className="pf-about-stat">
              <strong>18</strong>
              <span>years in technology</span>
            </div>
            <div className="pf-about-stat">
              <strong>Stanford</strong>
              <span>self-directed study, Silicon Valley</span>
            </div>
            <div className="pf-about-stat">
              <strong>Federal bids</strong>
              <span>agencies, ministries, public universities</span>
            </div>
            <div className="pf-about-stat">
              <strong>Automatrix</strong>
              <span>founder &amp; systems architect</span>
            </div>
          </div>
        </div>
      </section>

      <section id="philosophy" className="pf-section pf-philosophy">
        <div className="pf-container">
          <div className="pf-section-head">
            <span className="eyebrow">Philosophy</span>
            <h2>How systems get built here.</h2>
            <p>The principles behind every project in this portfolio.</p>
          </div>
          <div className="pf-principle-grid">
            <div className="pf-principle-card">
              <span className="pf-principle-index">01</span>
              <h3>Context is king.</h3>
              <p>The right context lets an LLM do almost any task well. Most of the engineering effort goes into getting that context right — not the model call itself.</p>
            </div>
            <div className="pf-principle-card">
              <span className="pf-principle-index">02</span>
              <h3>Deterministic over magic.</h3>
              <p>Automations should be auditable and reversible at every step, with a human-approval gate on anything high-stakes — not an opaque black box.</p>
            </div>
            <div className="pf-principle-card">
              <span className="pf-principle-index">03</span>
              <h3>Systems of agents.</h3>
              <p>The backbone of a modern company is a coordinated set of agents with memory and tools — not a single generic chatbot bolted onto a website.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="work" className="pf-section">
        <div className="pf-container">
          <div className="pf-section-head">
            <span className="eyebrow">Selected work</span>
            <h2>Systems shipped to production.</h2>
            <p>Client projects and internal products — pick one to see the full case.</p>
          </div>

          <div className="pf-case-grid">
            {CASES.map((c) => (
              <Link to={`/portfolio/${c.slug}`} className="pf-case-card" key={c.slug}>
                <div
                  className="pf-case-card-visual"
                  style={
                    c.thumbnail
                      ? { backgroundImage: `linear-gradient(180deg, rgba(0,0,0,.05), rgba(0,0,0,.55)), url(${c.thumbnail})` }
                      : { background: c.accent }
                  }
                >
                  {c.videoSrc && <span className="pf-case-play">▶</span>}
                </div>
                <div className="pf-case-card-body">
                  <div className="pf-case-card-meta">
                    <span className="pf-case-tag">{c.category}</span>
                    {c.region && <span className="pf-case-region">{c.region}</span>}
                  </div>
                  <h3>{c.title}</h3>
                  {c.client && <p className="pf-case-client">{c.client}</p>}
                  <p className="pf-case-tagline">{c.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="repos" className="pf-section pf-repos-section">
        <div className="pf-container">
          <div className="pf-section-head">
            <span className="eyebrow">Software Engineering &amp; Open Source</span>
            <h2>168 GitHub Repositories Catalog</h2>
            <p>
              Production architecture engineered by Lucas Alves (Morfeu333) — autonomous agents,
              SaaS, CRM automation (GHL/Meta/Chatwoot), 3D digital twins, and cloud infrastructure.
            </p>
          </div>
          <RepoCatalog />
        </div>
      </section>

      <section id="stack" className="pf-section">
        <div className="pf-container">
          <div className="pf-section-head">
            <span className="eyebrow">Technology landscape</span>
            <h2>Platforms, infrastructure, and AI stack.</h2>
            <p>Tools used across the projects above and other production delivery work.</p>
          </div>
          <div className="pf-stack-marquee">
            <div className="pf-stack-track-wrap">
              <div className="pf-stack-track">
                {[...STACK_ROW_A, ...STACK_ROW_A].map((entry, i) => (
                  <StackLogo entry={entry} key={`${entry.name}-${i}`} />
                ))}
              </div>
            </div>
            <div className="pf-stack-track-wrap">
              <div className="pf-stack-track pf-stack-track-reverse">
                {[...STACK_ROW_B, ...STACK_ROW_B].map((entry, i) => (
                  <StackLogo entry={entry} key={`${entry.name}-${i}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactSection />

      <footer className="pf-footer">
        <div className="pf-container pf-footer-grid">
          <div>
            <strong>AUTOMATRIX</strong>
            <br />
            AI infrastructure · automation architecture · agents
          </div>
          <div>Lucas F. N. Alves · Rio de Janeiro / São Paulo · Brazil · UTC−3</div>
        </div>
      </footer>
    </div>
  );
}
